import React, { useEffect, useState } from 'react';
import { Unstable_Grid2 as Grid } from '@mui/material';
import {
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
  Button as MuiButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import ConfirmDialog from '../../../Components/ConfirmDialog';
import { Dialog } from '../../../Components/MUIs';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PieChartOutline as PieChartOutlineIcon,
} from '@mui/icons-material';
import { AuthButton } from '../../../Components/MUIs';
import { BusinessGridCommon } from '../../../Components/BusinessGridCommon';
import { GetColumnDefs1, HelpDeskCategoryItems, HelpDeskStatusItems, HelpDeskCategoryStatsColors } from './HEL_BASIC_ColumnDefs';

const STATUS_CHIP_COLOR = {
  '10': { bgcolor: '#fff8e1', color: '#f57c00', border: '1px solid #ffe082' },
  '20': { bgcolor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' },
  '30': { bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
  '40': { bgcolor: '#fce4ec', color: '#c62828', border: '1px solid #f48fb1' },
};

const GetItemName = (items, value) =>
  items.find((item) => item.value === value)?.name || value || '-';

const CanPreviewAttachFile = (fileName) => {
  const ext = String(fileName || '').split('.').pop()?.toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'pdf', 'hwp', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt', 'log'].includes(
    ext
  );
};

const RenderAttachPreviewContent = (item) => {
  const previewUrl = item?.previewUrl || '';
  const fileName = item?.fileName || '';
  if (!previewUrl) {
    return (
      <Typography variant="body2" sx={{ color: '#999' }}>
        파일 정보가 없습니다.
      </Typography>
    );
  }

  const ext = fileName.split('.').pop()?.toLowerCase();

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) {
    return (
      <Box
        component="img"
        src={previewUrl}
        alt={fileName}
        sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
      />
    );
  }

  if (['pdf', 'hwp', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt', 'log'].includes(ext)) {
    const iframeUrl =
      ext === 'pdf' || ext === 'txt' || ext === 'log'
        ? previewUrl
        : previewUrl.replace(/\.(hwp|doc|docx|ppt|pptx|xls|xlsx)$/i, '.pdf');

    return (
      <Box
        component="iframe"
        src={iframeUrl}
        title={fileName}
        sx={{ width: '100%', height: '70vh', border: 'none' }}
      />
    );
  }

  return (
    <Typography variant="body2" sx={{ color: '#999' }}>
      지원하지 않는 형식입니다.
    </Typography>
  );
};

function HEL_BASIC(props) {
  const ColumnDefs1 = GetColumnDefs1();
  const IsAdmin = props.userInfo?.authCd === 'ADMIN';
  const IsDetail = Boolean(props.Form?.hdBoardNo);
  const StatusCd = props.Form?.statusCd || '10';
  const StatusStyle = STATUS_CHIP_COLOR[StatusCd] || STATUS_CHIP_COLOR['10'];
  const IsReadOnly = IsDetail && !props.IsEditMode;
  const CanEditPost = Boolean(props.IsDetailOwner);
  const CanPreviewAttach = IsDetail;
  const [IsGuideOpen, SetIsGuideOpen] = useState(true);
  const CategoryStats = props.CategoryStats || [];
  const StatsTotal = CategoryStats.reduce((sum, item) => sum + (item.value || 0), 0);
  const StatsChartData = CategoryStats.filter((item) => item.value > 0);

  useEffect(() => {
    if (props.IsWritePage && !IsDetail) {
      SetIsGuideOpen(true);
    }
  }, [props.IsWritePage, IsDetail]);

  const Conditions = [
    ...(IsAdmin
      ? [
          {
            type: 'combo',
            name: '회사',
            items: [{ value: '', name: '전체' }, ...(props.searchInfo?.coList ?? [])],
            value: props.searchInfo?.coCd ?? '',
            onChange: props.HandleCoChange,
          },
          {
            type: 'combo',
            name: '사업장',
            items: props.searchInfo?.custList ?? [],
            value: props.searchInfo?.custCd ?? '',
            onChange: props.HandleCustChange,
          },
        ]
      : []),
    {
      type: 'combo',
      name: '문의유형',
      items: HelpDeskCategoryItems,
      value: props.Filters.categoryCd,
      onChange: props.HandleCategoryChange,
    },
    {
      type: 'combo',
      name: '처리상태',
      items: HelpDeskStatusItems,
      value: props.Filters.statusCd,
      onChange: props.HandleStatusFilterChange,
    },
  ];

  return (
    <>
    <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
      {!props.IsWritePage && (
        <Grid xs={4} sm={8} md={12}>
          <AuthButton
            menuId="HelpDesk"
            searchYn="Y"
            searchBox="Y"
            addYn="Y"
            saveYn="N"
            deleteYn="N"
            printYn="N"
            placeholder="제목/내용/작성자 검색"
            searchClick={(text) => props.SearchLogic1(text)}
            addClick={props.AddLogic1}
            conditions={Conditions}
            grids={
              <BusinessGridCommon
                getRowId={(params) =>
                  String(params.data?.no ?? params.data?.hdBoardNo ?? '')
                }
                gridRef={props.GridRef1}
                rowData={props.RowData1}
                columnDefs={ColumnDefs1}
                rowSelection="multiple"
                addClick1={props.AddLogic1}
                deleteClick1={props.DeleteSelectedLogic1}
                setSelectedRowData={props.SetSelectedRowData1}
                setChangedRowData={props.SetChangedRowData1}
                refreshClick1={() => props.RefreshLogic1?.()}
                other1={
                  IsAdmin ? (
                    <Tooltip title="문의유형 통계" arrow placement="top">
                      <IconButton
                        color="primary"
                        sx={{
                          border: 1,
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          boxSizing: 'border-box',
                        }}
                        size="small"
                        variant="outlined"
                        onClick={props.OpenStatsLogic1}
                      >
                        <PieChartOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null
                }
                onCellClicked={(e) => {
                  const field = e?.column?.getColDef?.()?.field ?? e?.colDef?.field;
                  const isCheckboxClick = e?.event?.target?.type === 'checkbox';
                  if (field === 'selectRow' || isCheckboxClick) return;
                  props.HandleRowClick1(e?.data);
                }}
              />
            }
          />
        </Grid>
      )}

      {props.IsWritePage && (
        <Grid xs={4} sm={8} md={12}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '16px',
              border: '1px solid #eee',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            {/* 상단 헤더 */}
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                borderBottom: '1px solid #eee',
                bgcolor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 1.5,
                boxSizing: 'border-box',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                <IconButton size="small" onClick={props.CloseWritePage} sx={{ mr: 0.5 }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '20px', md: '24px' },
                    color: '#1a1a1a',
                    letterSpacing: '-0.5px',
                  }}
                >
                  {IsDetail ? '문의 상세' : '문의 작성'}
                </Typography>
              </Stack>
              <Box sx={{ flex: 1, minWidth: 8 }} />
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ flexShrink: 0, justifyContent: 'flex-end' }}
              >
                {!IsDetail && (
                  <MuiButton variant="contained" onClick={props.SaveLogic1}>
                    저장
                  </MuiButton>
                )}
                {IsDetail && IsReadOnly && CanEditPost && (
                  <MuiButton variant="contained" onClick={props.EditLogic1}>
                    수정
                  </MuiButton>
                )}
                {IsDetail && props.IsEditMode && CanEditPost && (
                  <>
                    <MuiButton variant="contained" onClick={props.SaveLogic1}>
                      저장
                    </MuiButton>
                    <MuiButton variant="outlined" onClick={props.CancelEditLogic1}>
                      취소
                    </MuiButton>
                  </>
                )}
                {IsDetail && (CanEditPost || IsAdmin) && (
                  <MuiButton variant="outlined" color="error" onClick={props.DeleteLogic1}>
                    삭제
                  </MuiButton>
                )}
              </Stack>
            </Box>

            <Stack
              direction="column"
              spacing={3}
              sx={{ width: '100%', px: { xs: 2, md: 3 }, py: 3, boxSizing: 'border-box' }}
            >
              {!IsDetail && (
                <Box
                  sx={{
                    width: '100%',
                    borderRadius: '12px',
                    bgcolor: '#eef4ff',
                    color: '#1a3a6b',
                    border: '1px solid #c5d9f7',
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => SetIsGuideOpen((prev) => !prev)}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <InfoOutlinedIcon sx={{ color: '#4e4caf', fontSize: 22 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        원활하고 빠른 업무 처리를 위해
                      </Typography>
                    </Stack>
                    <IconButton
                      size="small"
                      aria-label={IsGuideOpen ? '안내 접기' : '안내 펼치기'}
                      onClick={(e) => {
                        e.stopPropagation();
                        SetIsGuideOpen((prev) => !prev);
                      }}
                      sx={{ color: '#4e4caf' }}
                    >
                      {IsGuideOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Stack>

                  <Collapse in={IsGuideOpen}>
                    <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                      <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                        문의 내용에 아래 항목을 함께 적어 주시면 담당자가 상황을 정확히
                        파악하고{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          신속하게 처리
                        </Box>
                        하는 데 큰 도움이 됩니다.
                      </Typography>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.5,
                          '& li': { mb: 0.5, lineHeight: 1.65 },
                        }}
                      >
                        <li>
                          <strong>어떤 화면/메뉴</strong>에서 문제가 발생했는지 (관련 화면란에
                          입력해 주세요)
                        </li>
                        <li>
                          <strong>어떤 순서로 조작</strong>했을 때 발생하는지 (재현 방법)
                        </li>
                        <li>
                          화면에 표시된 <strong>오류 메시지 전체</strong>를 그대로 적어 주세요
                        </li>
                        <li>
                          <strong>오류 화면 캡처</strong> 또는 관련 이미지를 첨부파일로
                          올려 주세요
                        </li>
                        <li>
                          기대했던 결과와 실제로 나타난 결과가 다르다면,{' '}
                          <strong>두 가지를 모두</strong> 적어 주세요
                        </li>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* 메타 정보 */}
              <Box
                sx={{
                  width: '100%',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: '#f8f9fc',
                  border: '1px solid #eceef5',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  {IsReadOnly ? (
                    <Chip
                      label={GetItemName(HelpDeskCategoryItems, props.Form.categoryCd || 'QNA')}
                      size="small"
                      sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                    />
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}>
                      <InputLabel>문의유형</InputLabel>
                      <Select
                        label="문의유형"
                        value={props.Form.categoryCd || 'QNA'}
                        onChange={(e) =>
                          props.SetForm((prev) => ({ ...prev, categoryCd: e.target.value }))
                        }
                      >
                        {HelpDeskCategoryItems.filter((i) => i.value !== '').map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {IsAdmin ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      <FormControl size="small" sx={{ minWidth: 160, flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                        <InputLabel>처리상태</InputLabel>
                        <Select
                          label="처리상태"
                          value={StatusCd}
                          onChange={(e) =>
                            props.SetForm((prev) => ({ ...prev, statusCd: e.target.value }))
                          }
                        >
                          {HelpDeskStatusItems.filter((i) => i.value !== '').map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {IsDetail && (
                        <MuiButton
                          variant="outlined"
                          size="small"
                          onClick={props.SaveStatusLogic1}
                          sx={{ flexShrink: 0, height: 40 }}
                        >
                          상태 변경
                        </MuiButton>
                      )}
                    </Stack>
                  ) : (
                    <Chip
                      label={GetItemName(HelpDeskStatusItems, StatusCd)}
                      size="small"
                      sx={{ ...StatusStyle, fontWeight: 600, alignSelf: 'flex-start' }}
                    />
                  )}
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.5, sm: 2 }}
                  sx={{ mt: 2, pt: 2, borderTop: '1px solid #eceef5' }}
                >
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    작성자{' '}
                    <Box component="span" sx={{ color: '#222', fontWeight: 600 }}>
                      {props.Form.createNm || '-'}
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    등록일{' '}
                    <Box component="span" sx={{ color: '#222', fontWeight: 600 }}>
                      {String(props.Form.createDate || '').substring(0, 10) || '-'}
                    </Box>
                  </Typography>
                </Stack>
              </Box>

              {/* 제목 */}
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#555', mb: 1 }}
                >
                  제목
                </Typography>
                {IsReadOnly ? (
                  <Typography
                    sx={{
                      fontSize: { xs: '18px', md: '22px' },
                      fontWeight: 700,
                      color: '#222',
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                    }}
                  >
                    {props.Form.title || '-'}
                  </Typography>
                ) : (
                  <TextField
                    fullWidth
                    placeholder="문의 제목을 입력하세요"
                    value={props.Form.title || ''}
                    onChange={(e) =>
                      props.SetForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    InputProps={{
                      sx: {
                        fontSize: { xs: '18px', md: '22px' },
                        fontWeight: 700,
                        color: '#222',
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        bgcolor: '#fff',
                      },
                    }}
                  />
                )}
              </Box>

              {/* 화면명 (선택) */}
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#555', mb: 1 }}
                >
                  관련 화면 <Box component="span" sx={{ color: '#999', fontWeight: 400 }}>(선택)</Box>
                </Typography>
                {IsReadOnly ? (
                  <Typography variant="body1" sx={{ color: '#333' }}>
                    {props.Form.screenNm || '-'}
                  </Typography>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="예) 안전작업허가서, 교육관리"
                    value={props.Form.screenNm || ''}
                    onChange={(e) =>
                      props.SetForm((prev) => ({ ...prev, screenNm: e.target.value }))
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    }}
                  />
                )}
              </Box>

              {/* 본문 */}
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#555', mb: 1 }}
                >
                  문의 내용
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    border: '1px solid #e8e8e8',
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                  }}
                >
                  {IsReadOnly ? (
                    <Box sx={{ p: 2.5, minHeight: 200 }}>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          lineHeight: 1.85,
                          color: '#333',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {props.Form.content || '-'}
                      </Typography>
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      minRows={14}
                      placeholder={'문의 내용을 자세히 작성해 주세요.\n오류 화면, 재현 방법, 기대 결과 등을 함께 적어주시면 더 빠르게 답변드릴 수 있습니다.'}
                      value={props.Form.content || ''}
                      onChange={(e) =>
                        props.SetForm((prev) => ({ ...prev, content: e.target.value }))
                      }
                      InputProps={{
                        sx: {
                          fontSize: '16px',
                          lineHeight: 1.85,
                          color: '#333',
                          alignItems: 'flex-start',
                          py: 1,
                        },
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiInputBase-root': { alignItems: 'flex-start', width: '100%' },
                      }}
                    />
                  )}
                </Paper>
              </Box>

              {/* 첨부파일 */}
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: '#333',
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 18, color: '#4e4caf' }} />
                  첨부파일
                </Typography>
                {!IsReadOnly && (
                  <Box
                    onDrop={props.HandleFileDrop}
                    onDragOver={(e) => e.preventDefault()}
                    sx={{
                      width: '100%',
                      border: '2px dashed #d0d5e8',
                      borderRadius: '12px',
                      p: 2.5,
                      bgcolor: '#fafbff',
                      textAlign: 'center',
                      transition: 'border-color 0.2s, background-color 0.2s',
                      '&:hover': {
                        borderColor: '#4e4caf',
                        bgcolor: '#f5f6ff',
                      },
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 36, color: '#9aa3c7', mb: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      파일을 드래그하거나 아래 버튼으로 선택하세요
                    </Typography>
                    <MuiButton component="label" size="small" variant="outlined">
                      파일 선택
                      <input
                        hidden
                        type="file"
                        multiple
                        onChange={props.HandleFileSelect}
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp,.txt,.log"
                      />
                    </MuiButton>
                  </Box>
                )}

                {(props.AttachFiles || []).length > 0 && (
                  <Stack spacing={1} sx={{ mt: IsReadOnly ? 0 : 1.5 }}>
                    {(props.AttachFiles || []).map((file, idx) => (
                      <Stack
                        key={`${file.fileName}-${idx}`}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          px: 1.5,
                          py: 1,
                          bgcolor: '#fff',
                          borderRadius: '10px',
                          border: '1px solid #eee',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                          <AttachFileIcon sx={{ fontSize: 18, color: '#4e4caf', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            noWrap
                            onClick={() => {
                              if (CanPreviewAttach && CanPreviewAttachFile(file.fileName)) {
                                props.PreviewAttachLogic1?.(file);
                              }
                            }}
                            sx={{
                              color:
                                CanPreviewAttach && CanPreviewAttachFile(file.fileName)
                                  ? '#4e4caf'
                                  : '#333',
                              cursor:
                                CanPreviewAttach && CanPreviewAttachFile(file.fileName)
                                  ? 'pointer'
                                  : 'default',
                              textDecoration:
                                CanPreviewAttach && CanPreviewAttachFile(file.fileName)
                                  ? 'underline'
                                  : 'none',
                              '&:hover':
                                CanPreviewAttach && CanPreviewAttachFile(file.fileName)
                                  ? { color: '#303f9f' }
                                  : undefined,
                            }}
                          >
                            {file.fileName}
                          </Typography>
                        </Stack>
                        <Box sx={{ flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => props.DownloadAttachLogic1(file)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          {!IsReadOnly && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => props.RemoveAttachLogic1(idx)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                )}
                {IsReadOnly && (!props.AttachFiles || props.AttachFiles.length === 0) && (
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    첨부파일이 없습니다.
                  </Typography>
                )}
              </Box>

              {IsDetail && (
                <>
                  <Divider sx={{ width: '100%' }} />
                  <Box
                    sx={{
                      width: '100%',
                      mx: { xs: -2, md: -3 },
                      px: { xs: 2, md: 3 },
                      py: 3,
                      bgcolor: '#fafafa',
                      borderTop: '1px solid #eee',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ width: '100%', mb: 2 }}
                    >
                      <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: '#4e4caf' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                        답변 및 댓글
                      </Typography>
                      {(props.RowData2 || []).length > 0 && (
                        <Chip
                          label={(props.RowData2 || []).length}
                          size="small"
                          sx={{ height: 22, fontWeight: 700 }}
                        />
                      )}
                    </Stack>

                    <Stack direction="column" spacing={1.5} sx={{ width: '100%', mb: 2.5 }}>
                      {(props.RowData2 || []).length === 0 && (
                        <Box
                          sx={{
                            width: '100%',
                            py: 3,
                            textAlign: 'center',
                            borderRadius: '12px',
                            border: '1px dashed #ddd',
                            bgcolor: '#fff',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#999' }}>
                            아직 등록된 답변이 없습니다.
                          </Typography>
                        </Box>
                      )}
                      {(props.RowData2 || []).map((row) => {
                        const IsAdminReply = row.adminYn === 'Y';
                        return (
                          <Box
                            key={row.hdReplyNo}
                            sx={{
                              width: '100%',
                              p: 2,
                              borderRadius: '12px',
                              bgcolor: IsAdminReply ? '#eef4ff' : '#fff',
                              border: IsAdminReply ? '1px solid #c5d9f7' : '1px solid #eee',
                              boxSizing: 'border-box',
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Chip
                                label={IsAdminReply ? '시스템관리자' : row.createNm || '작성자'}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontWeight: 600,
                                  bgcolor: IsAdminReply ? '#4e4caf' : '#f0f0f0',
                                  color: IsAdminReply ? '#fff' : '#555',
                                }}
                              />
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                {String(row.createDate || '').substring(0, 16)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body1"
                              sx={{
                                color: '#333',
                                lineHeight: 1.75,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {row.content}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>

                    <Paper
                      elevation={0}
                      sx={{
                        width: '100%',
                        p: 2,
                        borderRadius: '12px',
                        border: '1px solid #e0e0e0',
                        bgcolor: '#fff',
                        boxSizing: 'border-box',
                      }}
                    >
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="추가 문의나 답변을 입력하세요"
                        value={props.ReplyText}
                        onChange={(e) => props.SetReplyText(e.target.value)}
                        sx={{
                          mb: 1.5,
                          width: '100%',
                          '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                        }}
                      />
                      <Stack direction="row" justifyContent="flex-end">
                        <MuiButton variant="contained" onClick={props.SaveReplyLogic1}>
                          댓글 등록
                        </MuiButton>
                      </Stack>
                    </Paper>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>
        </Grid>
      )}
    </Grid>
    <Dialog
      open={props.StatsOpen || false}
      onClose={props.CloseStatsLogic1}
      title="문의유형별 통계"
      maxWidth="sm"
      fullWidth
      useSaveButton={false}
      closeButtonName="닫기"
      content={
        <Box sx={{ width: '100%', py: 1 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            현재 조회 조건 기준 · 총 {StatsTotal}건
          </Typography>
          {StatsTotal === 0 ? (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                color: '#999',
                border: '1px dashed #ddd',
                borderRadius: 2,
              }}
            >
              <Typography variant="body2">조회된 문의가 없습니다.</Typography>
            </Box>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={StatsChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={105}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {StatsChartData.map((entry) => (
                      <Cell
                        key={entry.code}
                        fill={HelpDeskCategoryStatsColors[entry.code] || '#9e9e9e'}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => [`${value}건`, '건수']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {CategoryStats.map((item) => (
                  <Stack
                    key={item.code}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: '#fafafa',
                      border: '1px solid #eee',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: HelpDeskCategoryStatsColors[item.code] || '#9e9e9e',
                        }}
                      />
                      <Typography variant="body2">{item.name}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {item.value}건
                      {StatsTotal > 0
                        ? ` (${((item.value / StatsTotal) * 100).toFixed(1)}%)`
                        : ''}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </Box>
      }
    />
    <ConfirmDialog
      open={props.DeleteConfirmOpen || false}
      title="삭제"
      contentText={props.DeleteConfirmMessage || '삭제하시겠습니까?'}
      onSave={props.ConfirmDeleteLogic1}
      onClose={props.CloseDeleteConfirm}
      saveButtonName="삭제"
      closeButtonName="취소"
    />
    <Dialog
      open={props.AttachPreviewOpen || false}
      onClose={props.CloseAttachPreviewLogic1}
      title={props.AttachPreviewItem?.fileName || '첨부파일 미리보기'}
      maxWidth="lg"
      fullWidth
      useSaveButton={false}
      closeButtonName="닫기"
      content={
        <Box
          sx={{
            width: '100%',
            minHeight: 320,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#fafafa',
            borderRadius: 1,
            p: 1,
          }}
        >
          {RenderAttachPreviewContent(props.AttachPreviewItem)}
        </Box>
      }
    />
    </>
  );
}

export default HEL_BASIC;
